import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useStatusViewContext } from '../../../context/StatusViewContext';
import { FavoriteStationToggle } from '../favorites/FavoriteStationToggle';
import { StreamerBoxStatus } from '../streamer-box/StreamerBoxStatus';
import { StatusAvatar } from './StatusAvatar';
import { StatusIndicator } from './StatusIndicator';
import { StatusSharedStationBadge } from './StatusSharedStationBadge';
import { Station } from '@app/core/types/stations';

interface Props {
  readonly station: Station;
  readonly isOnline: boolean;
}

const Wrapper = styled(Box)`
  display: flex;
  align-items: center;
`;

/**
 * Header component for the Status card that displays station information,
 * favorite toggle, status indicator, and status badge.
 */
export function StatusHeader(p: Props) {
  const { station, isOnline } = p;
  const { compact } = useStatusViewContext();

  return (
    <Wrapper>
      <StatusAvatar station={station} isOnline={isOnline} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            gap: compact ? 1 : 1.5,
            margin: compact ? '0 0.5rem' : '0 1rem',
            overflow: 'hidden',
          }}
        >
          <Typography
            component="div"
            sx={{
              flex: '0 1 auto',
              minWidth: 0,
              color: 'text.primary',
              fontSize: compact ? '0.9rem' : '1.1rem',
              fontWeight: 'bold',
              lineHeight: '1.25rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {station.name}
          </Typography>
          <StatusSharedStationBadge station={station} compact={compact} />
          <Box sx={{ flexShrink: 0 }}>
            <FavoriteStationToggle stationId={station.id} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          minWidth: compact ? 'auto' : '7.5rem',
          marginLeft: 'auto',
          alignItems: 'center',
          gap: compact ? 0.5 : 1,
        }}
      >
        <StreamerBoxStatus stationId={station.id} compact={compact} />
        <StatusIndicator isOnline={isOnline} compact={compact} />
      </Box>
    </Wrapper>
  );
}
